
import { useState, type KeyboardEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getCategory } from "@/lib/data/categories";
import { CategorySlug } from "@/lib/types";
import { cn } from "@/lib/utils";

export default function ImageGallery({
  images,
  category,
  title,
}: {
  images: string[];
  category: CategorySlug;
  title: string;
}) {
  const [active, setActive] = useState(0);
  const cat = getCategory(category);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowRight") setActive((i) => Math.min(i + 1, images.length - 1));
    if (e.key === "ArrowLeft") setActive((i) => Math.max(i - 1, 0));
  };

  if (images.length === 0) {
    return (
      <div
        className="nm-grain relative aspect-square rounded-2xl overflow-hidden"
        style={{ background: cat?.gradient }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="font-display text-5xl font-semibold opacity-20 text-center px-8"
            style={{ color: cat?.accent }}
          >
            {cat?.name}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div tabIndex={0} onKeyDown={onKeyDown} className="focus:outline-none">
      <div
        className="relative aspect-square rounded-2xl overflow-hidden bg-black/20 touch-pan-y"
        onTouchStart={(e) => {
          const startX = e.touches[0].clientX;
          const handler = (ev: TouchEvent) => {
            const dx = ev.changedTouches[0].clientX - startX;
            if (dx < -40) setActive((i) => Math.min(i + 1, images.length - 1));
            if (dx > 40) setActive((i) => Math.max(i - 1, 0));
            window.removeEventListener("touchend", handler);
          };
          window.addEventListener("touchend", handler, { once: true });
        }}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={active}
            src={images[active]}
            alt={`${title} — photo ${active + 1}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
      </div>

      {images.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto nm-scrollbar-none">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={cn(
                "shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors",
                i === active ? "border-[var(--nm-blue-bright)]" : "border-transparent opacity-60 hover:opacity-100"
              )}
              aria-label={`View photo ${i + 1}`}
            >
              <img
                src={img}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
